import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ThresholdService from "../../../services/thresholdService";

//  Motif imports
import MotifTextLink from "@ey-xd/motif-react/TextLink";
import { MotifIconButton } from "@ey-xd/motif-react/Button";
import MotifTooltip from "@ey-xd/motif-react/Tooltip";

//  Icons imports
import iconInfo from "../../../../src/assets/images/info.svg";

/**
 * Internal imports
 */
import "./Projects.scss";
import NoProjects from "../noProjects/NoProjects";
import {
  setActiveProject,
  setActiveProjectUserRole,
  setProjectThresholds,
} from "../../../redux/userSlice";
import { groupConfig } from "../../../config/groupConfig";
import ProjectService from "../../../services/projectService";
import Utils from "../../../utils/Utils";

export default function Projects() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const thresholdService = new ThresholdService();
  const {
    user,
    projects,
    userGroupNamePlatform,
    platformAdminUsers,
    userGroupName,
  } = useSelector((state) => state.user);
  // Local state to manage projects with the new column
  const [thresholdProjects, setThresholdProjects] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const projectService = new ProjectService();
  let projectThresholdList = [];
  const utils = new Utils();

  useEffect(() => {
    if (
      userGroupNamePlatform !== null &&
      userGroupNamePlatform === groupConfig.groupNamePlatForm
    ) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
    // eslint-disable-next-line
  }, [userGroupNamePlatform]);

  useEffect(() => {
    const fetchAndUpdate = async () => {
      if (projects && projects.length > 0) {
        // Update the local state with the new column
        await getProjectsThreshold();
        if (projectThresholdList.length > 0) {
          const vThresholdProjects = addThresholdValue(projects);
          setThresholdProjects(vThresholdProjects);
          let finalProjects = vThresholdProjects;
          // Sort the final projects by createTS in descending order
          finalProjects.sort(
            (a, b) => new Date(b.createTS) - new Date(a.createTS)
          );

          dispatch(
            setProjectThresholds({
              thresholdProjects: finalProjects,
            })
          );
          setThresholdProjects(finalProjects);
        } else {
          setThresholdProjects(projects);
        }
      } else {
        setThresholdProjects(projects);
      }
    };
    fetchAndUpdate();
    // eslint-disable-next-line
  }, [projects, isAdmin, platformAdminUsers]);

  /**
   * Function to call API to get project thresholds list
   */
  const getProjectsThreshold = async () => {
    const thresholdProjects = await projectService.GetProjectThresholdList(
      user?.username
    );
    if (thresholdProjects?.length > 0) {
      projectThresholdList = thresholdProjects;
    }
  };

  /**
   * Function to add thresholdValue
   * @param {*} projects
   * @returns
   */
  const addThresholdValue = (projects) => {
    return projects.map((project) => {
      // Find the threshold item that matches the project's UUID
      const thresholdItem = projectThresholdList.find(
        (item) => item.id === project.project_UUID
      );

      let thresholdValue = "No";
      let globalThresholdValue;
      let globalThresholdPercent;

      if (thresholdItem) {
        if (
          thresholdItem.hasOwnProperty("globalThresholdValue") &&
          thresholdItem.hasOwnProperty("globalThresholdPercent")
        ) {
          if (
            thresholdItem.globalThresholdValue >= 0 &&
            thresholdItem.globalThresholdPercent >= 0
          ) {
            globalThresholdValue = thresholdItem.globalThresholdValue;
            globalThresholdPercent = thresholdItem.globalThresholdPercent;
            thresholdValue = "Yes";
          }
        }
      }

      return {
        ...project,
        thresholdValue,
        globalThresholdValue,
        globalThresholdPercent,
      };
    });
  };

  /**
   * Function to navigate to set threshold page
   * @param {*} projectId
   * @param {*} projectName
   */
  const navToThreshold = (projectId, projectName) => {
    navigate("/threshold");
  };

  /**
   * Function to navigate to project details
   * @param {*} projectId
   * @param {*} projectName
   */
  const navToProject = (projectId, projectName) => {
    navigate("/projectdetails");
  };

  /**
   * Function to navigate to project or threshold page when clicking on project
   * @param {*} project
   * @returns
   */
  const handleProjectNameClick = async (project) => {
    if (
      project.createdBy !== user.username &&
      project.role !== "Admin" &&
      project.thresholdValue !== "Yes"
    ) {
      return;
    }
    dispatch(
      setActiveProject({
        activeProject: project,
      })
    );
    dispatch(
      setActiveProjectUserRole({
        activeProjectUserRole: project?.role,
      })
    );
    const userId = user?.username;
    const userRole = userGroupName;
    const response = await thresholdService.getThresholdForProject(
      project.project_UUID,
      userId,
      userRole
    );
    if (response && !response.threshold_set) {
      navToProject(project.project_UUID, project.projectName);
    } else if (response && response.threshold_set) {
      navToThreshold(project.project_UUID, project.projectName);
    }
  };

  /**
   * Function to get class name for Project Outer layer
   * @param {*} vProject
   * @returns ClassName
   */
  const getClassNameOuterLayer = (vProject) => {
    let vClassName = "";
    if (vProject.role === "Admin" || vProject.createdBy === user?.username) {
      vClassName = "project-inner-div";
    } else if (vProject.thresholdValue !== "Yes") {
      vClassName = "is-disabled-external";
    } else {
      vClassName = "project-inner-div";
    }
    return vClassName;
  };

  /**
   * Function to get class name for Project Inner layer
   * @param {*} vProject
   * @returns ClassName
   */
  const getClassNameInnerLayer = (vProject) => {
    let vClassName = "";
    if (
      vProject.createdBy !== user?.username &&
      vProject.role !== "Admin" &&
      vProject.thresholdValue !== "Yes"
    ) {
      vClassName = "is-disabled";
    }
    return vClassName;
  };

  /**
   * Function to get class name for Project Link along with disabled class
   * @param {*} vProject
   * @param {*} vIndex
   * @returns
   */
  const getClassNameLink = (vProject, vIndex) => {
    let vClassName = "";
    if (vIndex !== 0) {
      vClassName = "project-div";
    } else {
      vClassName = "project-div no-border";
    }
    if (vProject.role === "Admin" || vProject.createdBy === user?.username) {
      vClassName = vClassName + "";
    } else if (vProject.thresholdValue !== "Yes") {
      vClassName = vClassName + " is-disabled";
    } else {
      vClassName = vClassName + "";
    }
    return vClassName;
  };

  return (() => {
    if (thresholdProjects && thresholdProjects.length === 0) {
      return <NoProjects />;
    } else if (thresholdProjects && thresholdProjects.length !== 0) {
      return thresholdProjects.map((project, index) => {
        return (
          <MotifTextLink
            href="#"
            className={getClassNameLink(project, index)}
            key={project.project_UUID}
            tabIndex="0"
            onClick={() => handleProjectNameClick(project)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleProjectNameClick(project);
            }}
          >
            <div className={getClassNameOuterLayer(project)}>
              <div className={getClassNameInnerLayer(project)}>
                <div className="project-name">{project.projectName}</div>
                <span className="project-date">
                  {"Created on: " +
                    utils.formatDateString(project.createTS, "Mmm DD, YYYY")}
                </span>
              </div>
              {project.createdBy !== user.username &&
                project.role !== "Admin" &&
                project.thresholdValue !== "Yes" && (
                  <MotifIconButton
                    aria-label="Info"
                    id="no-threshold-icon"
                    className="iconNoThreshold"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MotifTooltip
                      trigger={<img src={iconInfo} alt="No Threshold Icon" />}
                      hideCloseButton={true}
                      contentClassName="tooltipWithoutCloseCenterText"
                      allowClick={false}
                    >
                      <div>
                        Global thresholds not configured. Contact a <br></br>
                        Project Admin to set Global thresholds and<br></br>
                        enable access to the project.
                      </div>
                    </MotifTooltip>
                  </MotifIconButton>
                )}
            </div>
          </MotifTextLink>
        );
      });
    }
  })();
}
